import { Calendar } from '@seek/date-picker';
import { useTranslations } from '@vocab/react';
import {
  Box,
  IconDate,
  Stack,
  Text,
  IconChevron,
  Bleed,
  Inline,
  Strong,
  Secondary,
  IconCritical,
} from 'braid-design-system';
import { createRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import lysTranslations from 'src/pages/LinkYourSoftware/.vocab';
import { useClickOutside } from 'src/utils/useClickOutside';

import type { FormValues } from '../../../../../ContentState';

import * as styles from './GoLiveDate.css';

export const GoLiveDate = () => {
  const now = new Date();
  const maxDate = new Date();
  maxDate.setDate(now.getDate() + 366);

  const [menuOpen, setMenuOpen] = useState(false);
  const calendarRef = createRef<HTMLDivElement>();
  useClickOutside(calendarRef, () => {
    setMenuOpen(false);
  });

  const [startDate, setStartDate] = useState(now);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const { setValue } = useFormContext<FormValues>();
  const { t: lys } = useTranslations(lysTranslations);

  const { clearErrors } = useFormContext<FormValues>();
  return (
    <Box ref={calendarRef}>
      <Controller
        name="goLiveDate"
        rules={{
          required: lys('goLiveDateValidation'),
        }}
        render={({ field, fieldState }) => (
          <Stack space="small">
            <Text>
              <Strong>{lys('goLiveDate')}</Strong>
            </Text>
            <Text>
              <Secondary>{lys('solutionStartDate')}</Secondary>
            </Text>
            <Box>
              <Box
                id="lys-go-live-date"
                component="label"
                background="surface"
                className={styles.field}
                boxShadow={fieldState.error ? 'borderCritical' : 'borderField'}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <Text
                  baseline={false}
                  tone={selectedDate ? 'neutral' : 'secondary'}
                >
                  <Stack space="gutter">
                    <Box component="span" className={styles.text}>
                      <Inline space="xsmall">
                        <IconDate tone="neutral" />
                        <Box>
                          {selectedDate
                            ? new Date(field.value).toLocaleDateString('en-AU')
                            : 'dd/mm/yyyy'}
                        </Box>
                      </Inline>
                    </Box>
                  </Stack>
                </Text>
                <Box
                  display="flex"
                  alignItems="center"
                  transition="fast"
                  position="absolute"
                  right={0}
                  top={0}
                >
                  <Box
                    component="span"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="touchable"
                    width="touchable"
                  >
                    <Text baseline={false}>
                      <IconChevron
                        direction={menuOpen ? 'up' : 'down'}
                        alignY="lowercase"
                      />
                    </Text>
                  </Box>
                </Box>
              </Box>
              {menuOpen && (
                <Bleed bottom="large" right="large">
                  <Box paddingTop="xsmall">
                    <Box zIndex={1} position="absolute">
                      <Calendar
                        currentLocalDate={startDate}
                        deactivateStartDate={true}
                        maxDate={maxDate.toISOString().split('T')[0]}
                        minDate={now.toISOString().split('T')[0]}
                        onDateButtonClicked={(date) => {
                          clearErrors('goLiveDate');
                          setValue('goLiveDate', new Date(date).toISOString());
                          setSelectedDate(date);
                          setMenuOpen(false);
                        }}
                        onMonthClicked={(date) => {
                          setStartDate(new Date(date));
                        }}
                        value={{
                          endDate: selectedDate,
                          startDate: selectedDate,
                        }}
                      />
                    </Box>
                  </Box>
                </Bleed>
              )}
            </Box>
            {fieldState.error && (
              <Text
                id="lys-go-live-date-error-message"
                icon={<IconCritical />}
                tone="critical"
                size="small"
              >
                {fieldState.error.message}
              </Text>
            )}
          </Stack>
        )}
      />
    </Box>
  );
};
